import React, { useMemo, useCallback } from "react";
import StreamCard from "./StreamCard";
import "./VideoGrid.css";
import trans from "../../../utils";

// Calculate optimal grid dimensions based on stream count
const calculateGridDimensions = (count) => {
  if (count === 0) return { cols: 1, rows: 1 };
  if (count === 1) return { cols: 1, rows: 1 };
  if (count === 2) return { cols: 2, rows: 1 };
  if (count === 3) return { cols: 3, rows: 1 };
  if (count === 4) return { cols: 2, rows: 2 };
  if (count <= 6) return { cols: 3, rows: 2 };
  if (count <= 9) return { cols: 3, rows: 3 };
  if (count <= 12) return { cols: 4, rows: 3 };
  if (count <= 16) return { cols: 4, rows: 4 };
  if (count <= 20) return { cols: 5, rows: 4 };
  if (count <= 25) return { cols: 5, rows: 5 };
  if (count <= 30) return { cols: 6, rows: 5 };
  // 31-32 streams
  return { cols: 8, rows: 4 };
};

const VideoGrid = React.memo(({
  activeStreams,
  onRemoveStream,
  isPlaying,
  setIsPlaying,
}) => {
  const streamCount = activeStreams.length;

  // Calculate optimal grid dimensions
  const { cols, rows } = useMemo(() => {
    return calculateGridDimensions(streamCount);
  }, [streamCount]);

  // Create grid slots based on calculated dimensions
  const gridSlots = useMemo(() => {
    const totalSlots = cols * rows;
    const slots = Array(totalSlots).fill(null);
    
    // Assign streams to slots in order
    activeStreams.forEach((stream, index) => {
      if (index < totalSlots) {
        slots[index] = stream;
      }
    });
    
    return slots;
  }, [activeStreams, cols, rows]);

  const handleRemove = useCallback((streamId) => {
    onRemoveStream(streamId);
  }, [onRemoveStream]);

  // Generate dynamic grid style
  const gridStyle = useMemo(() => {
    return {
      gridTemplateColumns: `repeat(${cols}, 1fr)`,
      gridTemplateRows: `repeat(${rows}, 1fr)`,
    };
  }, [cols, rows]);

  if (streamCount === 0) {
    return (
      <div className="video-grid-container">
        <div className="empty-grid-message">
          <div className="empty-text">{trans("no active streams")}</div>
          <div className="empty-subtext">{trans("select a channel from the sidebar to start streaming")}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="video-grid-container">
      <div className="video-grid" style={gridStyle}>
        {gridSlots.map((stream, index) => (
          <div key={stream ? stream.id : `empty-${index}`} className="video-grid-slot">
            {stream ? (
              <StreamCard
                stream={stream}
                onRemove={() => handleRemove(stream.id)}
                isPlaying={isPlaying}
              />
            ) : (
              <div className="video-grid-placeholder">
                <div className="placeholder-content">
                  <div className="placeholder-play-icon">▶</div>
                  <div className="placeholder-text">{trans("live stream")}</div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
});

export default VideoGrid;
